import { autoinject, TaskQueue, bindable } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection, IForm } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { ContactLogService } from 'app/core/services/contact-log-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { ExpandEvent, EssTableCustomElement } from 'app/shared/elements/ess-table';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { IPopup } from 'app/shared/elements/popup-base';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { validate } from 'app/shared/elements/ess-rule';
import { IContactLogConfig } from 'app/core/models/essette-interfaces';
import { Toaster } from 'app/shared/lib/toaster';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { SaveOutcome } from 'app/um/components/contact-log/save-outcome';
import { localSort } from 'app/shared/lib/sorting';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { ContactLogMethodReloadEvent, ContactLogOutcomeReloadEvent } from 'app/um/lib/events';
import * as UM from 'app/um/models/essette-api';

const expandedOutcomesCache = new Map<string, Array<UM.AuthContactLogMethod>>();

@autoinject()
export class ContactLogOutcomes extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected expandedCacheKey: string = "um-manage-contact-log-outcomes";

    @bindable
    protected config: IContactLogConfig = {};

    @bindable
    protected rememberExpand: boolean = true;

    protected contactLogOutcome: UM.AuthContactLogOutcome | null = null;
    protected contactLogMethods: ReadonlyArray<UM.AuthContactLogMethod> | null = null;

    private contactLogOutcomeReload: Subscription | null = null;

    private sorting: Sorting = new Sorting();
    protected resultCount: number | null = 0;
    protected pageSize: number = 25;
    protected includeInactive: boolean = false;

    protected validate = validate;

    ////****************** Element References ***************************************////
    protected $table!: EssTableCustomElement<UM.AuthContactLogMethod[]>;
    protected $pager!: EssPagerCustomElement;

    protected $saveOutcomeModal!: IPopup;
    protected $form!: IForm;
    protected $outcomeForm!: Element;
    protected $saveOutcome!: SaveOutcome;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: ContactLogService,
        private readonly queue: TaskQueue,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.service.configure(this.config);
        this.contactLogOutcomeReload = this.events.subscribe(ContactLogOutcomeReloadEvent, (e: ContactLogOutcomeReloadEvent) => {
            this.reload();
        });
        this.reload();
    }

    protected detached(): void {
        if(this.contactLogOutcomeReload) {
            this.contactLogOutcomeReload.dispose();
        }
    }

    ////****************** Events ***************************************************////
    protected formSuccess({ detail }: FormSuccessCustomEvent) {
        this.$saveOutcomeModal.close();

        if(detail.form.reset) detail.form.reset();

        // reload grid and reset page
        this.reload();
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected applyLocalSort(event: SortChangeEvent, outcomes: (UM.AuthContactLogOutcome)[]): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        localSort(outcomes, sorting);
        if(this.sorting.entries.length == 0) {
            this.reload();
        };
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadContactLogMethods();
    }


    protected saveContactLogOutcome(outcome: UM.AuthContactLogOutcome): void {
        this.$saveOutcome.outcome = outcome;
        this.contactLogOutcome = outcome;

        this.$saveOutcomeModal.open();
    }

    protected saveContactLogOutcomeClose(): void {
        if(this.$form.reset) this.$form.reset();
        this.$saveOutcomeModal.close();
    }

    private restoreExpanded() {
        if(!this.rememberExpand) { return; }
        // we need to give aurelia a chance to bind the outcomes into the
        // table before we ask the table to expand the rows
        this.queue.queueTask(() => {
            if(!this.expandedCacheKey) { return; }
            const expandedOutcomes = expandedOutcomesCache.get(this.expandedCacheKey);
            if(expandedOutcomes !== undefined) {
                this.$table.restoreExpanded(expandedOutcomes);
            }
        });
    }

    protected trackExpanded({ detail }: ExpandEvent<UM.AuthContactLogMethod>) {
        if(!this.rememberExpand || !this.expandedCacheKey) { return; }
        expandedOutcomesCache.set(this.expandedCacheKey, detail.items);
    }

    protected containsSingleActiveOutcome(outcomes: ReadonlyArray<UM.AuthContactLogOutcome>): boolean {
        return outcomes.filter((outcome: UM.AuthContactLogOutcome) => outcome['isActive']).length === 1;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async activateContactLogOutcome(contactLogOutcome: UM.AuthContactLogOutcome): Promise<void> {
        // Show confirmation dialog only if deactivating
        if(contactLogOutcome.isActive) {
            const result = await confirmation.showDeactivate('core:contactLog.outcome.$long', 1);
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }

            if(!(result.dialogResponse === confirmation.options.OK)) {
                return;
            }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateContactLogOutcome(contactLogOutcome.id!, !contactLogOutcome.isActive);
            isSuccessful = response.isSuccessful;
            this.reload();
        } catch(error) {
            throw error;
        } finally {
            if(isSuccessful) this.events.publish(new ContactLogMethodReloadEvent());
            this.toaster.standard.activate('core:contactLog.outcome.$long', !contactLogOutcome.isActive, isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    private async loadContactLogMethods(): Promise<void> {

        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.sorting,
            includeInactive: this.includeInactive
        };

        const includes: string[] = ['outcomes'];
        const withOutcomes: boolean = true;

        try {
            this.state.beginLoad();

            const response = await this.service.loadContactLogMethods(options, includes, withOutcomes);

            this.contactLogMethods = response.results;
            this.resultCount = response.count;
            this.restoreExpanded();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
